<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Matrículas</h5>
      <b-button
        v-if="isPlanoStandard && (hasPermission('adicionar_matriculas') || hasPermission('operar_matriculas'))"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-plus-circle-fill mr-2" title="Nova Matrícula"></i>
      </b-button>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3 align-items-center" style="gap: 10px;">
      <span>Filtros:</span>
      <input v-model="filters.matricula" placeholder="Matrícula" class="form-control" @input="filtrarMatriculas" />
      <input v-model="filters.aluno" placeholder="Aluno" class="form-control" @input="filtrarMatriculas" />
      <input v-model="filters.responsavel" placeholder="Responsável" class="form-control" @input="filtrarMatriculas" />
      <select v-model="filters.direitoImagem" class="form-control" @change="filtrarMatriculas">
        <option value="">Imagem (Todos)</option>
        <option value="Sim">Imagem: Sim</option>
        <option value="Não">Imagem: Não</option>
      </select>
      <select v-model="filters.nivel_id" aria-label="Nível" class="form-control" @change="filtrarMatriculas">
        <option value="">Nível (Todos)</option>
        <option v-for="nivel in niveis" :key="nivel.id" :value="nivel.id">{{ nivel.nivel }}</option>
      </select>
      <select v-model="filters.status" class="form-control" @change="filtrarMatriculas">
        <option value="">Todos</option>
        <option value="Ativo">Ativo</option>
        <option value="Arquivado">Arquivado</option>
        <option value="Trancado">Trancado</option>
        <option value="Encerrado">Encerrado</option>
        <option value="Cancelado">Cancelado</option>
      </select>
      <b-button variant="secondary" @click="limparFiltros" class="d-flex align-items-center justify-content-center">
        <i class="bi bi-funnel mr-2" title="Limpar Filtro"></i>
      </b-button>
    </div>

    <!-- Tabela customizada sem b-table -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th><button class="btn btn-link p-0" @click="sort('id')">Matrícula {{ arrow('id') }}</button></th>
          <th><button class="btn btn-link p-0" @click="sort('contrato')">Contrato {{ arrow('contrato') }}</button></th>
          <th><button class="btn btn-link p-0" @click="sort('aluno')">Aluno {{ arrow('aluno') }}</button></th>
          <th><button class="btn btn-link p-0" @click="sort('responsavel')">Responsável {{ arrow('responsavel') }}</button></th>
          <th>
            <button class="btn btn-link p-0" @click="sort('direito_imagem')">
              Autoriza Imagem {{ arrow('direito_imagem') }}
            </button>
          </th>
          <th><button class="btn btn-link p-0" @click="sort('status')">Status {{ arrow('status') }}</button></th>
          <th><button class="btn btn-link p-0" @click="sort('vencimento')">Vencimento {{ arrow('vencimento') }}</button></th>
          <th>Nível</th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in matriculasFiltradas" :key="row.id">
          <td>{{ row.id }}</td>
          <td>{{ row.numero_contrato }}</td>
          <td>{{ row.aluno_nome }}</td>
          <td>{{ row.responsavel_nome }}</td>
          <td>
            <span :class="row.direito_imagem ? 'text-success' : 'text-danger'">
              {{ row.direito_imagem ? 'Sim' : 'Não' }}
            </span>

            <i
              v-if="hasPermission('alterar_direito_imagem_matriculas')"
              class="bi mx-1"
              :class="row.direito_imagem ? 'bi-toggle-on text-success' : 'bi-toggle-off text-secondary'"
              :title="row.direito_imagem ? 'Alterar para Não' : 'Alterar para Sim'"
              style="cursor: pointer; font-size: 1.1rem;"
              @click="alterarDireitoImagem(row)"
            ></i>
          </td>
          <td><span :class="statusClass(row.status_exibido)">{{ row.status_exibido }}</span></td>
          <td>{{ globalUtils.formatarData(row.termino_vigencia) }}</td>
          <td>{{ row.nivel_nome || '—' }}</td>
          <td class="text-end">
            <router-link
              v-if="hasPermission('detalhar_matriculas')"
              :to="`/operacional/matriculas/${row.id}/detalhar`"
            >
              <i class="bi bi-eye-fill text-primary mx-1" title="Detalhar"></i>
            </router-link>

            <!-- Cancelar Matrícula -->
            <i
              v-if="hasPermission('cancelar_matriculas') 
                  && !row.cancelada
                  && !['Encerrado','Arquivado','Cancelado'].includes(row.status_exibido)"
              class="bi bi-x-octagon-fill text-danger mx-1"
              title="Cancelar Matrícula"
              style="cursor:pointer"
              @click="abrirModalCancelar(row)"
            ></i>

            <!-- Reverter Cancelamento -->
            <i
              v-if="hasPermission('reverter_cancelamento_matriculas') 
                    && row.cancelada"
              class="bi bi-arrow-counterclockwise text-success mx-1"
              title="Reverter Cancelamento"
              style="cursor:pointer"
              @click="reverterCancelamento(row)"
            ></i>

            <!-- Trancar -->
            <i
              v-if="hasPermission('trancar_matriculas') && row.status_exibido !== 'Trancado'"
              class="bi bi-lock-fill mx-1"
              :title="row.status_exibido === 'Ativo' ? 'Trancar Matrícula' : 'Trancar indisponível'"
              :style="{
                color: row.status_exibido === 'Ativo' ? 'brown' : 'lightgray',
                cursor: row.status_exibido === 'Ativo' ? 'pointer' : 'default'
              }"
              @click="row.status_exibido === 'Ativo' && abrirModalTrancar(row)"
            ></i>

            <!-- Destrancar -->
            <i
              v-if="hasPermission('destrancar_matriculas') && row.status_exibido === 'Trancado'"
              class="bi bi-unlock-fill mx-1 text-success"
              title="Antecipar Destranca"
              @click="abrirModalDestrancar(row)"
            ></i>

            <router-link
              v-if="hasPermission('alterar_matriculas') || hasPermission('operar_matriculas')"
              :to="`/operacional/matriculas/${row.id}`"
            >
              <i class="bi bi-pencil-fill text-warning mx-1" title="Editar"></i>
            </router-link>

            <i
              v-if="hasPermission('excluir_matriculas')"
              class="bi bi-trash-fill text-danger mx-1"
              title="Excluir"
              @click="excluirMatricula(row.id)"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>

      <!-- Paginação -->
      <div class="d-flex justify-content-between align-items mt-3">
        <!-- resumo -->
        <div>
          Mostrando {{ startRow }} a {{ endRow }} de {{ totalRegistros }} registros
        </div>

        <div class="d-flex align-items" style="gap: .5rem;">
                  <b-form-select
            v-model="limitePorPagina"
            :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
            size="sm"
            style="width: 6rem"
            @change="paginaAtual = 1; carregarMatriculas()"
          />
        </div>

        <!-- selectors + paginação -->
        <div class="d-flex align-items">
          <b-pagination
            v-model="paginaAtual"
            :per-page="limitePorPagina"
            :total-rows="totalRegistros"
            size="sm"
            limit="5"
            @input="carregarMatriculas"
          />
        </div>
      </div>

    <!-- Modais -->
    <TrancarMatricula :mostrar="mostrarTrancarModal" :matricula="matriculaSelecionada" @fechar="mostrarTrancarModal=false" @salvou="onTrancamentoSalvo" />
    <DestrancarMatricula :mostrar="exibirModalDestrancar" :matricula="matriculaSelecionada" @fechar="exibirModalDestrancar=false" @salvou="onDestrancamentoSalvo" />
    <CancelarMatricula :mostrar="mostrarCancelarModal" :matricula="matriculaSelecionada" @fechar="mostrarCancelarModal=false" @salvou="onCancelamentoSalvo" />

    <!-- Modal temporário para ajuste de datas -->
    <ModalAjustarDatas
      :mostrar="mostrarModalAjustarDatas"
      @fechar="mostrarModalAjustarDatas = false"
      @aplicou="onAjustesAplicados"
    />

  </div>
</template>

<script>
import axios from '@/services/axios';
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next';
import TrancarMatricula from '@/components/modals/TrancarMatricula.vue';
import DestrancarMatricula from '@/components/modals/DestrancarMatricula.vue';
import CancelarMatricula from '@/components/modals/CancelarMatricula.vue';
import globalUtils from '@/utils/globalUtils.js';

import ModalAjustarDatas from '@/components/modals/ModalAjustarDatas.vue'; // <-- NOVO


export default {
  components: { BButton, TrancarMatricula, DestrancarMatricula, CancelarMatricula, BPagination, BFormSelect, ModalAjustarDatas },
  data() {
    return {
      matriculas: [],
      niveis: [],
      paginaAtual: 1,
      limitePorPagina: 10,
      totalPaginas: 1,
      totalRegistros: 0,                // <-- novo
      pageOptions: [10, 15, 20, 25, 50], // <-- dropdown de limites
      currentSort: { key: 'id', order: 'asc' },
      filters: { nivel_id: '', matricula: '', aluno: '', responsavel: '', direitoImagem: '', status: 'Ativo' },
      permissions: [],
      mostrarTrancarModal: false,
      exibirModalDestrancar: false,
      mostrarCancelarModal: false,
      matriculaSelecionada: null,
      globalUtils,
      mostrarModalAjustarDatas: false, // <-- NOVO
    };
  },

  computed: {
    isPlanoStandard() {
      return (this.$globalState?.companyPlan || '').toUpperCase() === 'STANDARD';
    },

    matriculasFiltradas() {
      return this.matriculas.filter(m =>
        !this.filters.status || m.status_exibido === this.filters.status
      );
    },

    startRow() {
      return (this.paginaAtual - 1) * this.limitePorPagina + 1
    },

    endRow() {
      return Math.min(this.paginaAtual * this.limitePorPagina, this.totalRegistros)
    }
  },
  

  watch: {
    paginaAtual() {
      this.carregarMatriculas();   // dispara ao mudar de página
    }
  },


  methods: {
    statusClass(status) {
      if (status === 'Ativo') return 'text-success';
      if (status === 'Trancado') return 'text-danger';
      if (status === 'Encerrado') return 'text-brown';
      if (status === 'Arquivado') return 'text-primary';
      if (status === 'Cancelado') return 'text-danger';
      return '';
    },
    async carregarNiveis() {
      try {
        const { data } = await axios.get('/cadastros/niveis-turmas?page=1&limit=10000');
        this.niveis = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Erro ao carregar níveis:', error);
      }
    },

    async carregarMatriculas() {
      try {
        const res = await axios.get('/operacional/matriculas', {
          params: {
            nivel_id: this.filters.nivel_id || undefined,
            page: this.paginaAtual,
            limit: this.limitePorPagina,
            matricula:   this.filters.matricula   || undefined,
            aluno:       this.filters.aluno       || undefined,
            responsavel: this.filters.responsavel || undefined,
            direito_imagem: this.filters.direitoImagem || undefined,
            status:      this.filters.status      || undefined,
            sortBy:      this.currentSort.key,
            sortOrder:   this.currentSort.order
          }
        });
        // destruturação dos campos de paginação
        const { data, totalPages, total } = res.data;

        // mapeia os itens, adicionando seq
        this.matriculas = data.map((item, i) => ({
          ...item,
          seq: i + 1 + (this.paginaAtual - 1) * this.limitePorPagina
        }));

        // atualiza o total de páginas e registros
        this.totalPaginas   = totalPages;
        this.totalRegistros = total;

      } catch (e) {
        console.error('Erro ao carregar matrículas:', e);
      }
    },

    async alterarDireitoImagem(row) {
      if (!row || !row.id) return;

      const novoValor = !row.direito_imagem;
      const textoNovoValor = novoValor ? 'Sim' : 'Não';

      const confirmou = await (this.$confirm
        ? this.$confirm(
            `Confirma alterar a autorização de imagem da matrícula ${row.id} para "${textoNovoValor}"?`,
            'Confirmação'
          )
        : Promise.resolve(
            window.confirm(
              `Confirma alterar a autorização de imagem da matrícula ${row.id} para "${textoNovoValor}"?`
            )
          )
      );

      if (!confirmou) return;

      try {
        await axios.patch(`/operacional/matriculas/${row.id}/direito-imagem`, {
          direito_imagem: novoValor
        });

        this.$toast && this.$toast.success('Autorização de imagem alterada com sucesso!');
        await this.carregarMatriculas();

      } catch (e) {
        console.error('Erro ao alterar autorização de imagem:', e);

        const mensagem =
          e.response?.data?.error ||
          'Não foi possível alterar a autorização de imagem.';

        if (this.$alert) {
          await this.$alert(mensagem, 'Erro');
        } else {
          alert(mensagem);
        }
      }
    },

    sort(col) {
      if (this.currentSort.key===col) this.currentSort.order = this.currentSort.order==='asc'?'desc':'asc';
      else { this.currentSort.key=col; this.currentSort.order='asc'; }
      this.carregarMatriculas();
    },
    arrow(col) {
      return this.currentSort.key===col
        ? this.currentSort.order==='asc'?'▲':'▼'
        : '';
    },

    filtrarMatriculas() {
      this.paginaAtual = 1;
      this.carregarMatriculas();
    },

    limparFiltros() {
      this.filters = { nivel_id: '', matricula:'', aluno:'', responsavel:'', direitoImagem:'', status:'' };
      this.paginaAtual = 1;
      this.carregarMatriculas();
    },
    
    paginaAnterior() { if(this.paginaAtual>1){this.paginaAtual--;this.carregarMatriculas();} },
    proximaPagina() { if(this.paginaAtual<this.totalPaginas){this.paginaAtual++;this.carregarMatriculas();} },
    alterarLimite() { this.paginaAtual=1;this.carregarMatriculas(); },

    async excluirMatricula(id) {
      console.log('excluir', id);

      // Valida o parâmetro id
      if (!id) {
        await this.$alert('ID da matrícula inválido.', 'Erro');
        return;
      }

      try {
        // Exibe o modal de confirmação
        const confirmou = await this.$confirm(
          'Tem certeza que deseja excluir esta matrícula?',
          'Confirmação'
        );

        if (!confirmou) {
          return; // Usuário cancelou a ação
        }

        // Opcional: Ativar estado de carregamento
        this.loading = true;

        // Faz a requisição DELETE
        await axios.delete(`/operacional/matriculas/${id}`);

        // Atualiza a listagem de matrículas
        await this.carregarMatriculas();
        this.$toast.success('Matrícula excluída com sucesso!');
      } catch (error) {
        console.error('Erro ao excluir matrícula:', error);

        let mensagem = 'Erro inesperado ao excluir matrícula.';
        if (error.response) {
          // Personaliza a mensagem com base no status HTTP
          if (error.response.status === 404) {
            mensagem = 'Matrícula não encontrada.';
          } else if (error.response.status === 403) {
            mensagem = 'Você não tem permissão para excluir esta matrícula.';
          } else if (error.response.data && error.response.data.error) {
            mensagem = error.response.data.error;
          }
        } else if (error.request) {
          mensagem = 'Falha na conexão com o servidor. Verifique sua rede.';
        }

        // Exibe o alerta com a mensagem de erro
        await this.$alert(mensagem, 'Não permitido');
      } finally {
        // Opcional: Desativar estado de carregamento
        this.loading = false;
      }
    },

    
    irParaCadastro() { this.$router.push("/operacional/matriculas/novo"); },
    abrirModalTrancar(m) { this.matriculaSelecionada=m;this.mostrarTrancarModal=true; },
    onTrancamentoSalvo() { this.mostrarTrancarModal=false;this.carregarMatriculas(); },
    abrirModalDestrancar(m) { this.matriculaSelecionada=m;this.exibirModalDestrancar=true; },
    onDestrancamentoSalvo() { this.exibirModalDestrancar=false;this.carregarMatriculas(); },
    abrirModalCancelar(m) { this.matriculaSelecionada = m; this.mostrarCancelarModal = true; },
    onCancelamentoSalvo() { this.mostrarCancelarModal = false; this.carregarMatriculas(); },

    abrirModalAjustarDatas() { this.mostrarModalAjustarDatas = true; }, // <-- NOVO
    onAjustesAplicados() {
      // após o apply no modal, recarrega a listagem
      this.mostrarModalAjustarDatas = false;
      this.carregarMatriculas();
    },

    async reverterCancelamento(row) {
      const confirmou = await (this.$confirm
        ? this.$confirm('Confirma reverter o cancelamento desta matrícula?', 'Confirmação')
        : Promise.resolve(window.confirm('Confirma reverter o cancelamento desta matrícula?'))
      );

      if (!confirmou) return;

      try {
        const userName = (this.$globalState && this.$globalState.userName) ? this.$globalState.userName : 'Usuário';

        await axios.post(`/operacional/matriculas-cancelamentos/${row.id}/reverter`, {
          nome_usuario: userName
        });

        this.$toast && this.$toast.success('Cancelamento revertido com sucesso!');
        await this.carregarMatriculas();
      } catch (e) {
        console.error('Erro ao reverter cancelamento:', e);
        this.$alert && this.$alert(e.response?.data?.error || e.response?.data?.detail || 'Não foi possível reverter o cancelamento.', 'Erro');
      }
    },

  },
  mounted() { this.carregarNiveis(); this.carregarMatriculas(); }
};
</script>

<style scoped>
.text-brown { color: brown; }

:deep(.pagination.pagination-sm .page-link) {
  padding: rem rem;
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}

</style>
